import { MhpDetails, Nullable, RequestState } from 'modules/types/common';
import { PrimaryProvider } from 'store/visitSummary/types';
import { ShareClinicNotesState } from './reducers/clinicNotesList';

// Clinic Notes Detail Types
export interface ClinicNotesDetailState {
  isFetching: boolean;
  error: Nullable<Error>;
  data: Nullable<ClinicNotesDetailData>;
}

export interface ClinicNotesDetailData extends ClinicNotesListItem {
  id: string;
  content: string;
  contentType: string;
  location: {
    locationId: string;
    locationNcid: Nullable<string>;
    locationName: string;
  };
  primaryProvider: PrimaryProvider;
  resourceType: string;
}

// Clinic Notes Filter Types
export enum ClinicNotesFilterType {
  provider = 'provider',
  noteCategory = 'noteCategories',
  dateTime = 'dateTime',
  dateRange = 'dateRange'
}

export enum FilterDateTimeOptions {
  ONE_MONTH = '30',
  THREE_MONTHS = '90',
  ONE_YEAR = '360'
}

// values to be in a dayjs object
export const dateTimeMappers = {
  [FilterDateTimeOptions.ONE_MONTH]: [1, 'month'],
  [FilterDateTimeOptions.THREE_MONTHS]: [3, 'months'],
  [FilterDateTimeOptions.ONE_YEAR]: [1, 'year']
};

// values to be used in a lodash get or set function
export const filterOptionValueGetters: any = {
  [ClinicNotesFilterType.provider]: 'provider.providerName',
  [ClinicNotesFilterType.noteCategory]: 'noteCategory',
  [ClinicNotesFilterType.dateTime]: 'value',
  [ClinicNotesFilterType.dateRange]: 'value'
};

/**
 * FROM date in the past
 * TO date in the present/future
 */
export interface FilterDateRange {
  from: string;
  to: string;
}

export interface ClinicNotesFilter {
  filterType: string | ClinicNotesFilterType;
  value: string | FilterDateRange;
}

export interface ClinicNotesFilterOption extends ClinicNotesListItem {
  filterType: ClinicNotesFilterType;
  checked: boolean;
  title?: string;
  value?: any;
}

export interface DateTimeOption {
  id: string | ClinicNotesFilterType;
  filterType: ClinicNotesFilterType;
  checked: boolean;
  title?: string;
  value?: any;
}

export interface ClinicNotesFilterOptions {
  // providers: ClinicNotesFilterOption[];
  noteCategories: ClinicNotesFilterOption[];
  dateTime: DateTimeOption[];
}

// Clinic Notes Sort Types
export enum ClinicNotesSortBy {
  date = 'Date',
  name = 'Name'
}

export const sortByMapper = {
  [ClinicNotesSortBy.date]: 'indexDate',
  [ClinicNotesSortBy.name]: 'description'
};

export const sortByDirectionMapper = {
  [ClinicNotesSortBy.date]: 'desc',
  [ClinicNotesSortBy.name]: 'asc'
};

// Clinic Notes List Types
export interface ClinicNotesListState {
  isFetching: boolean;
  error: Nullable<Error>;
  data: Nullable<ClinicNotesListItem[]>;
  sortBy: ClinicNotesSortBy;
  filters: ClinicNotesFilter[];
  filterOptions: Nullable<ClinicNotesFilterOptions>;
  lastUpdated: string | undefined;
  isSending: boolean;
  requestState: RequestState;
}

export interface DocumentLinkMeta {
  mimeType?: string;
  documentId?: string;
}

export interface DocumentTypeMeta {
  system?: string;
  code?: string;
  text?: string;
}

export interface ClinicNotesListItem {
  adapterSource?: string;
  contentType?: string;
  createDate?: string;
  indexDate: string;
  description: string;
  links: DocumentLinkMeta[];
  id: string;
  encounterId: string;
  docStatus: string;
  status: string;
  type: DocumentTypeMeta[];
  noteType: string;
  noteCategory: string;
  mhpDetails: MhpDetails;
}

export interface ClinicNotesDownloadState {
  loading: boolean;
  response: Nullable<{
    jobId: number;
    statusCode: number;
    bytesWritten: number;
  }>;
  error: Nullable<Error>;
}

export interface ClinicNotesState {
  detail: ClinicNotesDetailState;
  download: ClinicNotesDownloadState;
  list: ClinicNotesListState;
  shareClinicNotes: ShareClinicNotesState;
}
